import { useState, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { VoiceRecorder, RecordingData } from "capacitor-voice-recorder";

interface UseVoiceRecordingOptions {
  onRecordingComplete?: (audioBlob: Blob, mimeType: string) => void;
  onError?: (error: string) => void;
}

/**
 * Converts a base64 string to a Blob.
 */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export const useVoiceRecording = ({
  onRecordingComplete,
  onError,
}: UseVoiceRecordingOptions = {}) => {
  const [isRecording, setIsRecording] = useState(false);

  // For web fallback
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>("audio/webm");

  const isNative = Capacitor.isNativePlatform();

  /**
   * Start recording using native plugin on Android/iOS, web fallback otherwise.
   */
  const startRecording = useCallback(async () => {
    if (isNative) {
      // ===== NATIVE PATH (capacitor-voice-recorder) =====
      try {
        // Check permission first
        const permResult = await VoiceRecorder.hasAudioRecordingPermission();
        let hasPermission = permResult.value;

        if (!hasPermission) {
          const reqResult = await VoiceRecorder.requestAudioRecordingPermission();
          hasPermission = reqResult.value;
        }

        if (!hasPermission) {
          console.log("[VoiceRecording] Native permission denied");
          onError?.("PERMISSION_DENIED");
          return;
        }

        await VoiceRecorder.startRecording();
        setIsRecording(true);
        console.log("[VoiceRecording] Native recording started");
      } catch (err: any) {
        console.error("[VoiceRecording] Native startRecording error:", err);
        const msg = err?.message?.toLowerCase() || "";
        if (
          msg.includes("permission") ||
          msg.includes("denied") ||
          msg.includes("not allowed")
        ) {
          onError?.("PERMISSION_DENIED");
        } else if (
          msg.includes("busy") ||
          msg.includes("in use") ||
          msg.includes("unavailable")
        ) {
          onError?.("MICROPHONE_UNAVAILABLE");
        } else {
          onError?.(err?.message || "Failed to start recording");
        }
      }
    } else {
      // ===== WEB PATH (MediaRecorder) =====
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          onError?.("Microphone access is not available on this device.");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

        mimeTypeRef.current = mimeType;
        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          stream.getTracks().forEach((track) => track.stop());

          if (chunksRef.current.length === 0) {
            onError?.("No audio recorded");
            return;
          }

          const audioBlob = new Blob(chunksRef.current, { type: mimeType });
          onRecordingComplete?.(audioBlob, mimeType);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err: unknown) {
        console.error("Microphone access error (web):", err);
        const error = err as {
          name?: string;
          message?: string;
          code?: string;
          constraint?: string;
        };

        const errorName = error?.name;
        const errorMessage = error?.message?.toLowerCase() || "";

        const isPermissionError =
          errorName === "NotAllowedError" ||
          errorName === "PermissionDeniedError" ||
          errorMessage.includes("permission") ||
          errorMessage.includes("not allowed") ||
          errorMessage.includes("denied") ||
          error?.code === "NotAllowedError" ||
          error?.constraint === "Permission denied";

        const isDeviceBusyError =
          errorName === "NotReadableError" ||
          errorMessage.includes("could not start") ||
          errorMessage.includes("device") ||
          errorMessage.includes("track") ||
          errorMessage.includes("busy") ||
          errorMessage.includes("in use");

        if (isPermissionError) {
          onError?.("PERMISSION_DENIED");
        } else if (isDeviceBusyError) {
          onError?.("MICROPHONE_UNAVAILABLE");
        } else {
          onError?.(error?.message || "Failed to access microphone");
        }
      }
    }
  }, [isNative, onRecordingComplete, onError]);

  /**
   * Stop recording and deliver the audio blob.
   */
  const stopRecording = useCallback(async () => {
    if (!isRecording) return;

    if (isNative) {
      try {
        const result: RecordingData = await VoiceRecorder.stopRecording();
        setIsRecording(false);
        console.log("[VoiceRecording] Native recording stopped", {
          mimeType: result.value.mimeType,
          duration: result.value.msDuration,
        });

        const audioBlob = base64ToBlob(
          result.value.recordDataBase64,
          result.value.mimeType
        );
        onRecordingComplete?.(audioBlob, result.value.mimeType);
      } catch (err: any) {
        console.error("[VoiceRecording] Native stopRecording error:", err);
        setIsRecording(false);
        onError?.(err?.message || "Failed to stop recording");
      }
    } else {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    }
  }, [isRecording, isNative, onRecordingComplete, onError]);

  /**
   * Cancel recording without saving.
   */
  const cancelRecording = useCallback(async () => {
    if (!isRecording) return;

    if (isNative) {
      try {
        await VoiceRecorder.stopRecording();
      } catch {
        // ignore
      }
      setIsRecording(false);
    } else {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        setIsRecording(false);
      }
    }
  }, [isRecording, isNative]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};
